/**
 * POBoxManagementPage — `/mail/pobox`.
 *
 * Live operator surface backed by service-mail `/v1/pobox/*`. Lists boxes,
 * filters by outlet/status/tier, opens a "New PO Box" modal, opens a "Rent"
 * modal for AVAILABLE rows, and a "Cancel rental" confirm for RENTED rows.
 */
import { type JSX, useEffect, useState } from 'react';
import { Inbox, UserPlus, X, Plus, Loader2 } from 'lucide-react';

import { PageHeader } from '../shared/StaffUI';
import {
  cancelPoBoxRental,
  createPoBox,
  listPoBoxes,
  rentPoBox,
  type PoBoxDto,
  type PoBoxStatus,
  type PoBoxTier,
} from '../../lib/api/pobox';

const STATUS_TONE: Record<PoBoxStatus, string> = {
  AVAILABLE: 'bg-success-light text-success',
  RENTED:    'bg-blue-100 text-blue-700',
  RESERVED:  'bg-amber-100 text-amber-700',
  RETIRED:   'bg-gray-100 text-gray-700',
};

const TIERS: readonly PoBoxTier[] = ['SMALL', 'MEDIUM', 'LARGE'];
const STATUSES: readonly PoBoxStatus[] = ['AVAILABLE', 'RENTED', 'RESERVED', 'RETIRED'];

export function POBoxManagementPage(): JSX.Element {
  const [rows, setRows] = useState<readonly PoBoxDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterOutlet, setFilterOutlet] = useState('');
  const [filterStatus, setFilterStatus] = useState<PoBoxStatus | ''>('');
  const [filterTier, setFilterTier] = useState<PoBoxTier | ''>('');
  const [showCreate, setShowCreate] = useState(false);
  const [rentTarget, setRentTarget] = useState<PoBoxDto | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function refresh(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const res = await listPoBoxes({
        ...(filterOutlet && { outletCode: filterOutlet }),
        ...(filterStatus && { status: filterStatus }),
        ...(filterTier && { tier: filterTier }),
        pageSize: 100,
      });
      setRows(res.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load PO Boxes');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // note: deps intentionally narrow — rule not enabled in this config
  }, [filterOutlet, filterStatus, filterTier]);

  async function handleCancel(box: PoBoxDto): Promise<void> {
    if (!window.confirm(`Cancel the active rental for ${box.boxNumber}?`)) return;
    setBusyId(box.id);
    try {
      await cancelPoBoxRental(box.id);
      await refresh();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to cancel rental');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="PO Box Management"
        description="Allocation register for outlet PO Boxes."
        actions={
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark"
            data-testid="pobox-new-btn"
          >
            <Plus className="w-4 h-4" /> New PO Box
          </button>
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 bg-white border border-border rounded-xl p-4">
        <div>
          <label className="block text-[10px] uppercase text-muted mb-1" htmlFor="filter-outlet">Outlet code</label>
          <input
            id="filter-outlet"
            type="text"
            placeholder="NG-LAG-VI-001"
            value={filterOutlet}
            onChange={(e) => setFilterOutlet(e.target.value)}
            className="border border-border rounded px-3 py-1.5 text-sm font-mono"
            data-testid="pobox-filter-outlet"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase text-muted mb-1" htmlFor="filter-status">Status</label>
          <select
            id="filter-status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as PoBoxStatus | '')}
            className="border border-border rounded px-3 py-1.5 text-sm"
            data-testid="pobox-filter-status"
          >
            <option value="">Any</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase text-muted mb-1" htmlFor="filter-tier">Tier</label>
          <select
            id="filter-tier"
            value={filterTier}
            onChange={(e) => setFilterTier(e.target.value as PoBoxTier | '')}
            className="border border-border rounded px-3 py-1.5 text-sm"
          >
            <option value="">Any</option>
            {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-muted ml-2" />}
      </div>

      {error && (
        <div className="bg-danger-light border border-danger text-danger text-sm rounded-xl p-3">
          {error}
        </div>
      )}

      <div className="bg-white border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm" data-testid="pobox-table">
          <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
            <tr>
              <th className="text-left px-4 py-2"><Inbox className="inline w-3 h-3" /> Box</th>
              <th className="text-left px-4 py-2">Outlet</th>
              <th className="text-left px-4 py-2">Tier</th>
              <th className="text-left px-4 py-2">Fee/mo (₦)</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-right px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.length === 0 && !loading && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-muted text-xs">No PO Boxes match the filters.</td></tr>
            )}
            {rows.map((b) => (
              <tr key={b.id} className="hover:bg-gray-50" data-testid={`pobox-row-${b.boxNumber}`}>
                <td className="px-4 py-2 font-mono text-xs font-bold">{b.boxNumber}</td>
                <td className="px-4 py-2 text-xs font-mono">{b.outletCode}</td>
                <td className="px-4 py-2 text-xs">{b.tier}</td>
                <td className="px-4 py-2 text-xs">{(b.monthlyFeeMinor / 100).toFixed(2)}</td>
                <td className="px-4 py-2">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_TONE[b.status]}`} data-testid={`pobox-status-${b.boxNumber}`}>
                    {b.status}
                  </span>
                </td>
                <td className="px-4 py-2 text-right">
                  {b.status === 'AVAILABLE' ? (
                    <button
                      type="button"
                      onClick={() => setRentTarget(b)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded bg-nipost-green text-white hover:bg-nipost-green-dark"
                      data-testid={`pobox-rent-${b.boxNumber}`}
                    >
                      <UserPlus className="w-3 h-3" /> Rent
                    </button>
                  ) : b.status === 'RENTED' ? (
                    <button
                      type="button"
                      onClick={() => void handleCancel(b)}
                      disabled={busyId === b.id}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold rounded border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                      data-testid={`pobox-cancel-${b.boxNumber}`}
                    >
                      <X className="w-3 h-3" /> Cancel rental
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateBoxModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); void refresh(); }} />}
      {rentTarget && (
        <RentBoxModal
          box={rentTarget}
          onClose={() => setRentTarget(null)}
          onRented={() => { setRentTarget(null); void refresh(); }}
        />
      )}
    </div>
  );
}

interface CreateBoxModalProps {
  onClose: () => void;
  onCreated: () => void;
}

function CreateBoxModal({ onClose, onCreated }: CreateBoxModalProps): JSX.Element {
  const [boxNumber, setBoxNumber] = useState('');
  const [outletCode, setOutletCode] = useState('NG-LAG-VI-001');
  const [tier, setTier] = useState<PoBoxTier>('SMALL');
  const [monthlyFeeNgn, setMonthlyFeeNgn] = useState('5000');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const minor = Math.round(Number(monthlyFeeNgn) * 100);
      await createPoBox({ boxNumber, outletCode, tier, monthlyFeeMinor: minor });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="pobox-create-modal">
      <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-4">
        <h3 className="font-semibold text-lg">New PO Box</h3>
        {error && <div className="text-sm text-danger">{error}</div>}
        <div>
          <label className="block text-xs text-muted mb-1" htmlFor="new-box-number">Box number</label>
          <input id="new-box-number" type="text" value={boxNumber} onChange={(e) => setBoxNumber(e.target.value)} className="w-full border border-border rounded px-3 py-2 font-mono" data-testid="pobox-create-number" />
        </div>
        <div>
          <label className="block text-xs text-muted mb-1" htmlFor="new-outlet">Outlet code</label>
          <input id="new-outlet" type="text" value={outletCode} onChange={(e) => setOutletCode(e.target.value)} className="w-full border border-border rounded px-3 py-2 font-mono" data-testid="pobox-create-outlet" />
        </div>
        <div>
          <label className="block text-xs text-muted mb-1" htmlFor="new-tier">Tier</label>
          <select id="new-tier" value={tier} onChange={(e) => setTier(e.target.value as PoBoxTier)} className="w-full border border-border rounded px-3 py-2">
            {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-muted mb-1" htmlFor="new-fee">Monthly fee (₦)</label>
          <input id="new-fee" type="number" step="0.01" value={monthlyFeeNgn} onChange={(e) => setMonthlyFeeNgn(e.target.value)} className="w-full border border-border rounded px-3 py-2" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm border border-border rounded">Cancel</button>
          <button type="button" onClick={() => void submit()} disabled={busy || !boxNumber} className="px-3 py-2 text-sm bg-nipost-green text-white rounded disabled:opacity-50" data-testid="pobox-create-submit">
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface RentBoxModalProps {
  box: PoBoxDto;
  onClose: () => void;
  onRented: () => void;
}

function RentBoxModal({ box, onClose, onRented }: RentBoxModalProps): JSX.Element {
  const [customerId, setCustomerId] = useState('cust-' + box.id.slice(0, 8));
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('+234');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await rentPoBox(box.id, {
        customerId,
        customerName,
        customerPhone,
        monthlyFeeMinor: box.monthlyFeeMinor,
        startDate,
      });
      onRented();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Rent failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="pobox-rent-modal">
      <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-4">
        <h3 className="font-semibold text-lg">Rent {box.boxNumber}</h3>
        {error && <div className="text-sm text-danger">{error}</div>}
        <div>
          <label className="block text-xs text-muted mb-1" htmlFor="rent-customer-id">Customer ID</label>
          <input id="rent-customer-id" type="text" value={customerId} onChange={(e) => setCustomerId(e.target.value)} className="w-full border border-border rounded px-3 py-2 font-mono" data-testid="pobox-rent-customer-id" />
        </div>
        <div>
          <label className="block text-xs text-muted mb-1" htmlFor="rent-name">Customer name</label>
          <input id="rent-name" type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)} className="w-full border border-border rounded px-3 py-2" data-testid="pobox-rent-name" />
        </div>
        <div>
          <label className="block text-xs text-muted mb-1" htmlFor="rent-phone">Phone</label>
          <input id="rent-phone" type="text" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} className="w-full border border-border rounded px-3 py-2" data-testid="pobox-rent-phone" />
        </div>
        <div>
          <label className="block text-xs text-muted mb-1" htmlFor="rent-start">Start date</label>
          <input id="rent-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full border border-border rounded px-3 py-2" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm border border-border rounded">Cancel</button>
          <button type="button" onClick={() => void submit()} disabled={busy || !customerName} className="px-3 py-2 text-sm bg-nipost-green text-white rounded disabled:opacity-50" data-testid="pobox-rent-submit">
            {busy ? 'Renting…' : 'Confirm rent'}
          </button>
        </div>
      </div>
    </div>
  );
}
